// Welke persoon hoort bij welke Dagstart-rij — op record-id, niet op voornaam.
//
// /Api/dagstart groepeert op "Voornaam Assignee": een rij heet "Bram", niet "Bram Zweers".
// Het scherm zocht daar vervolgens e-mail (agenda-uren, nieuwe taak) en functie (discipline)
// bij op VOORNAAM uit de personenlijst. Met vijf Brams (één medewerker, vier freelancers)
// won de laatste of de eerste in de lijst: de rij van Bram Zweers kon de agenda-uren of de
// discipline van een freelancer krijgen, en freelancers delen bovendien de postbus
// freelance@fullframe.nl — die agenda is van niemand in het bijzonder.
//
// Hier wordt elke rij één keer aan een persoonsrecord gekoppeld:
//   1. kandidaten = personen met die voornaam die de engine überhaupt als rij toont
//      (medewerker/stagiair/systeem — freelancers krijgen nooit een eigen rij);
//   2. precies één kandidaat → die persoon; meer → dubbelzinnig, dan geen agenda en geen
//      e-mail in plaats van die van een willekeurige naamgenoot;
//   3. levert de engine per taak `assigneeIds` (record-ids), dan splitsen we een gedeelde
//      voornaam-rij in één rij per persoon, elk met een uniek label ("Bram Z.", "Bram K.").
// Zonder botsing blijft het label gewoon de voornaam — voor iedereen verandert er niets.

import { persoonLabels, gedeeldeEmails, voornaam } from '../CapacityOverview/capaciteitPersonen';

const klein = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
const typeTekst = (p) => klein(Array.isArray(p?.type) ? p.type.join(', ') : p?.type);

/** Kan deze persoon een eigen Dagstart-rij hebben? (spiegelt ASSIGNEE_TYPES in de engine) */
export const kanRijHebben = (p) => {
  const t = typeTekst(p);
  return !t || /medewerker|stagiair|systeem/.test(t);
};

/** E-mail (kleine letters) die echt van deze ene persoon is; '' bij geen of een gedeelde postbus. */
export function eigenEmail(p, gedeeld) {
  const e = klein(p?.email);
  return e && !gedeeld?.has(e) ? e : '';
}

/**
 * Voornaam-rij → kandidaat-personen. Eerst op voornaam; valt dat leeg (planningsbakken
 * heten soms naar hun volledige naam) dan op volledige naam.
 */
export function kandidatenVoor(naam, personen) {
  const n = klein(naam);
  if (!n) return [];
  const lijst = (personen || []).filter(kanRijHebben);
  const opVoornaam = lijst.filter((p) => klein(voornaam(p)) === n);
  return opVoornaam.length ? opVoornaam : lijst.filter((p) => klein(p?.name) === n);
}

/**
 * Deel de serverdata opnieuw in per persoon.
 *
 * @param {{assignees?: object[], tasksByAssignee?: Object<string, object[]>}} data
 * @param {object[]} personen  /Api/planner-todo/persons
 * @returns {{assignees: object[], tasksByAssignee: Object<string, object[]>}}
 *   assignees: server-rij + `naam` (uniek label, sleutel van tasksByAssignee), `serverNaam`
 *   (voornaam zoals de engine hem kent), `persoonId`, `email` (alleen als eigen postbus),
 *   `functie`, `dubbelzinnig` (true als de rij niet aan één persoon te koppelen is).
 *   Taken krijgen `persoonId` mee als hun rij één persoon is.
 */
export function deelInPerPersoon(data, personen) {
  const perNaam = data?.tasksByAssignee || {};
  const serverRijen = data?.assignees?.length
    ? data.assignees
    : Object.keys(perNaam).map((naam) => ({ naam }));
  const gedeeld = gedeeldeEmails(personen);

  // Labels over álle naamgenoten die een rij kunnen hebben, zodat een label niet afhangt
  // van wie er toevallig taken heeft in dit venster (venster, backlog en horizon moeten
  // dezelfde sleutels opleveren).
  const labels = persoonLabels((personen || []).filter(kanRijHebben));

  const assignees = [];
  const tasksByAssignee = {};
  const voegToe = (rij, taken) => {
    // Botst een label toch met een bestaande rij (bv. een bak die toevallig zo heet),
    // dan de taken samenvoegen in plaats van stil overschrijven.
    if (tasksByAssignee[rij.naam]) {
      const al = new Set(tasksByAssignee[rij.naam].map((t) => t.id));
      tasksByAssignee[rij.naam].push(...taken.filter((t) => !al.has(t.id)));
      return;
    }
    assignees.push(rij);
    tasksByAssignee[rij.naam] = taken;
  };
  const metPersoon = (rij, p, naam, taken) => ({
    rij: {
      ...rij, naam, serverNaam: rij.naam, persoonId: p.id,
      email: eigenEmail(p, gedeeld), functie: p.function || '', dubbelzinnig: false,
    },
    taken: taken.map((t) => ({ ...t, persoonId: p.id })),
  });

  serverRijen.forEach((rij) => {
    const taken = perNaam[rij.naam] || [];
    const kandidaten = kandidatenVoor(rij.naam, personen);

    if (kandidaten.length === 1) {
      const { rij: r, taken: t } = metPersoon(rij, kandidaten[0], rij.naam, taken);
      voegToe(r, t);
      return;
    }

    const leeg = { ...rij, serverNaam: rij.naam, persoonId: null, email: '', functie: '', dubbelzinnig: kandidaten.length > 1 };
    const kanSplitsen = kandidaten.length > 1 && taken.some((t) => Array.isArray(t.assigneeIds));
    if (!kanSplitsen) { voegToe(leeg, taken); return; }

    const ids = new Set(kandidaten.map((p) => p.id));
    kandidaten.forEach((p) => {
      const eigen = taken.filter((t) => (t.assigneeIds || []).includes(p.id));
      if (!eigen.length) return;
      const { rij: r, taken: t } = metPersoon(rij, p, labels[p.id] || rij.naam, eigen);
      voegToe(r, t);
    });
    // Taken die bij geen van de kandidaten horen (legacy-record, persoon niet in de lijst)
    // blijven zichtbaar onder de oude voornaam — niets mag verdwijnen.
    const rest = taken.filter((t) => !(t.assigneeIds || []).some((id) => ids.has(id)));
    if (rest.length) voegToe(leeg, rest);
  });

  return { assignees, tasksByAssignee };
}

/**
 * Agenda per rij-label: uren en afspraken van de eigen postbus van de gekoppelde persoon.
 * Dubbelzinnige rijen en gedeelde postbussen krijgen niets — liever geen agenda dan die
 * van een naamgenoot. `events === null` betekent: Graph gaf geen afspraken terug.
 */
export function agendaPerRij(assignees, uren, events) {
  const perNaam = {};
  const eventsPerNaam = events ? {} : null;
  (assignees || []).forEach((a) => {
    if (!a?.email) return;
    perNaam[a.naam] = uren?.[a.email] || {};
    if (eventsPerNaam && Array.isArray(events[a.email])) eventsPerNaam[a.naam] = events[a.email];
  });
  return { perNaam, eventsPerNaam };
}

/**
 * Het persoonsrecord achter een taak, voor de "Toegewezen"-keuzelijst. Op id waar dat kan;
 * op naam alleen als die naam precies één persoon aanwijst — anders '' (de lijst toont dan
 * de naam uit de taak, in plaats van stil de eerste naamgenoot als gekozen te tonen).
 */
export function huidigePersoonId(taak, personen) {
  const lijst = personen || [];
  const bestaat = (id) => id && lijst.some((p) => p.id === id);
  if (bestaat(taak?.persoonId)) return taak.persoonId;
  const opId = (taak?.assigneeIds || []).find(bestaat);
  if (opId) return opId;
  const naam = (taak?.assignees || [])[0] || taak?.assignee || '';
  const kandidaten = kandidatenVoor(naam, lijst);
  if (kandidaten.length === 1) return kandidaten[0].id;
  if (kandidaten.length > 1) return '';
  const n = klein(naam);
  const opVoorvoegsel = n ? lijst.filter((p) => klein(p?.name).startsWith(n)) : [];
  return opVoorvoegsel.length === 1 ? opVoorvoegsel[0].id : '';
}

/**
 * Het assignee-deel van een POST /Api/tasks, op record-id. De engine accepteert
 * `Assignee: [recId]` direct; `assigneeEmail` is alleen de terugval als er geen id is.
 * Zonder beide kiest de engine stil de ingelogde gebruiker — dat mag hier nooit, want een
 * gedeelde postbus (freelance@) of een dubbelzinnige voornaam-rij heeft geen e-mail en de
 * taak zou dan bij de planner zelf belanden. Daarom: onbekend → null, en de aanroeper
 * meldt dat eerlijk in plaats van te posten.
 *
 * @param {{persoonId?: string|null, assigneeIds?: string[], email?: string|null}} bron
 * @returns {{Assignee: string[]}|{assigneeEmail: string}|null}
 */
export function assigneeVoorPost(bron) {
  if (bron?.persoonId) return { Assignee: [bron.persoonId] };
  const ids = (bron?.assigneeIds || []).filter(Boolean);
  if (ids.length) return { Assignee: ids };
  if (bron?.email) return { assigneeEmail: bron.email };
  return null;
}

/**
 * Per persoon de taken zonder wat voor deze kijker verborgen is (privé van een collega).
 * Rijen waar niets uit valt houden hun eigen array, zodat gememoiseerde tijdlijnen niet
 * opnieuw rekenen; deze functie hoort in een useMemo, niet per render per rij.
 *
 * @param {Object<string, object[]>} perPersoon
 * @param {(taakId: string) => boolean} verborgen
 */
export function zonderVerborgen(perPersoon, verborgen) {
  const uit = {};
  Object.entries(perPersoon || {}).forEach(([naam, taken]) => {
    const lijst = taken || [];
    uit[naam] = lijst.some((t) => verborgen(t.id)) ? lijst.filter((t) => !verborgen(t.id)) : lijst;
  });
  return uit;
}
